import type { Logger } from './types.ts'

/**
 * A logger that discards everything. The default, and what the browser keeps: no app registers a
 * logger there, so `failure()`/`success()` emit nothing rather than writing to the console.
 */
const noopLogger: Logger = {
  info: () => {},
  warn: () => {},
  error: () => {},
  debug: () => {},
  child: () => noopLogger,
  flush: () => Promise.resolve(),
}

let current: Logger = noopLogger

/**
 * Register the process-wide logger — the instance `getGlobalLogger()` resolves to, and therefore
 * what `failure()`/`success()` emit through.
 *
 * `createLogger` (from `@mntn/logger/node`) only constructs an instance; until a composition root
 * passes it here, the global stays the silent no-op. Call once, at the root. Last call wins; a
 * second call replaces the instance silently.
 */
export const setGlobalLogger = (next: Logger) => {
  current = next
}

/**
 * A stable wrapper whose methods resolve `current` when they are **called**, never when the
 * wrapper is captured. `select-api` registers its logger inside `main()`, long after every module
 * has imported this file; a wrapper that captured the instance at import would hold the no-op
 * forever, and — since nothing calls `failure()` at module scope — would do so silently.
 */
const globalLogger: Logger = {
  info: (message, context) => current.info(message, context),
  warn: (message, context) => current.warn(message, context),
  error: (message, context) => current.error(message, context),
  debug: (message, context) => current.debug(message, context),
  child: context => current.child(context),
  flush: () => current.flush(),
}

/**
 * The process-wide logger, as registered by `setGlobalLogger` — distinct from any app-local
 * instance a composition root builds with `createLogger`.
 *
 * Safe to call — or capture — at module scope, before any root has registered: the returned
 * wrapper resolves the registered instance at call time.
 */
export const getGlobalLogger = () => globalLogger
